import { Button, ButtonColors } from '@chiroup/components/Button';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { AdHocReportRun } from '@chiroup/core/types/AdHocReport.type';
import dayjs from 'dayjs';
import { useMemo } from 'react';

type Props = {
  runs: AdHocReportRun[];
  isLoading: boolean;
  onViewRun: (run: AdHocReportRun) => void;
  fetchingRunId: string | null;
  onDeleteRun: (runId: string) => void;
  deletingRunIds: Set<string>;
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'complete':
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
          Complete
        </span>
      );
    case 'processing':
      return (
        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Processing
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
          Error
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
          {status}
        </span>
      );
  }
};

const formatRuntime = (run: AdHocReportRun): string => {
  const ms = run.runtime;

  if (ms == null || ms < 0) return '-';
  const totalHundredths = Math.floor(ms / 10);
  const hh = Math.floor(totalHundredths / 360000);
  const mm = Math.floor((totalHundredths % 360000) / 6000);
  const ss = Math.floor((totalHundredths % 6000) / 100);
  const cs = totalHundredths % 100;

  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
};

const AdHocRunHistory = ({
  runs,
  isLoading,
  onViewRun,
  fetchingRunId,
  onDeleteRun,
  deletingRunIds,
}: Props) => {
  const columns = useMemo<ColumnDef<AdHocReportRun, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        meta: { className: 'whitespace-nowrap' },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => statusBadge(row.original.status),
        enableSorting: false,
      },
      {
        accessorKey: 'rowCount',
        header: 'Rows',
        cell: ({ row }) => row.original.rowCount ?? '-',
        enableSorting: false,
        meta: { align: 'right' as const },
      },
      {
        id: 'runtime',
        accessorFn: row => row.runtime ?? -1,
        header: 'Runtime',
        cell: ({ row }) => <span className="font-mono">{formatRuntime(row.original)}</span>,
        enableSorting: false,
        meta: { align: 'right' as const },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => dayjs(row.original.createdAt).format('MM/DD/YYYY h:mm A'),
        enableSorting: false,
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => {
          const run = row.original;

          return (
            <div className="flex justify-end gap-2">
              {run.status === 'complete' && (
                <Button
                  text="View"
                  onClick={e => {
                    e.stopPropagation();
                    onViewRun(run);
                  }}
                  color={ButtonColors.plainWithBorder}
                  loading={fetchingRunId === run.id}
                  disabled={!!fetchingRunId || deletingRunIds.has(run.id)}
                  className="h-6 px-2.5 py-0 text-xs"
                />
              )}
              <Button
                text="Delete"
                onClick={e => {
                  e.stopPropagation();
                  onDeleteRun(run.id);
                }}
                color={ButtonColors.plainWithBorder}
                loading={deletingRunIds.has(run.id)}
                disabled={deletingRunIds.has(run.id)}
                className="h-6 px-2.5 py-0 text-xs"
              />
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [onViewRun, fetchingRunId, onDeleteRun, deletingRunIds]
  );

  if (isLoading && !runs.length) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-4">Loading run history...</p>;
  }

  if (!runs.length) {
    return null;
  }

  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold mb-2">Run History</h3>
      <DataTable columns={columns} data={runs} size="compact" />
    </div>
  );
};

export default AdHocRunHistory;
